import { Mark } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: {
      setComment: (threadId: string) => ReturnType;
      unsetComment: () => ReturnType;
    };
  }
}

/** Event dispatched when a commented range is clicked. */
export interface ShowCommentDetail {
  threadId: string;
  top: number;
  left: number;
}

/**
 * Comment mark — highlights a text range that has an associated comment thread.
 * Carries ONLY the `threadId`; the thread content (replies, resolved state)
 * lives in the Yjs `comments` map (see comment-threads.ts). Renders as
 * `<span data-comment-id class="nula-editor-comment">`, reusing the existing
 * comment styling in globals.css.
 *
 * Commands: setComment(threadId) applies the mark over the selection;
 * unsetComment() removes it.
 */
export const CommentMark = Mark.create({
  name: 'comment',
  // Allow other marks (bold, etc.) to coexist on commented text.
  inclusive: false,

  addAttributes() {
    return {
      threadId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-comment-id'),
        renderHTML: (attrs) =>
          attrs.threadId ? { 'data-comment-id': attrs.threadId as string } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-comment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', { ...HTMLAttributes, class: 'nula-editor-comment' }, 0];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('commentClick'),
        props: {
          // Deterministic click detection via ProseMirror (not a React DOM
          // listener, which races + can bind to a detached editor view). When a
          // commented range is clicked, dispatch `nula-show-comment` with the
          // threadId + screen coords; EditorComments opens the thread panel.
          handleClick: (view, pos, event) => {
            const mark = view.state.doc
              .resolve(pos)
              .marks()
              .find((m) => m.type.name === 'comment');
            const threadId = mark?.attrs.threadId as string | undefined;
            if (!threadId) return false;
            const rect = (event.target as HTMLElement).getBoundingClientRect();
            window.dispatchEvent(
              new CustomEvent<ShowCommentDetail>('nula-show-comment', {
                detail: { threadId, top: rect.bottom + 6, left: rect.left },
              }),
            );
            return false; // don't consume — let the caret place normally
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setComment:
        (threadId: string) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ({ commands }: any) =>
          commands.setMark(this.name, { threadId }),
      unsetComment:
        () =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ({ commands }: any) =>
          commands.unsetMark(this.name),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;
  },
});
